import { WIDGET_CONFIG } from '../../common/ambient';

/**
 * Check if a parameter is a battery field
 */
export const isBatteryParam = (param) => {
  return WIDGET_CONFIG.battery.fields.includes(param);
};

/**
 * Determine if this device/param uses flipped battery logic (1 = low instead of 0 = low)
 * Based on logic from BatteryWidget.js
 */
export const isLowBatteryFlipped = (param, device) => {
  if (!device || !device.lastData) return false;

  const stationType = device.lastData.stationtype;
  // weatherbridge devices have flipped battery logic (1 = low)
  if (stationType === 'weatherbridge' || stationType === 'weatherbridgepro') {
    return true;
  }

  // leak and lightning batteries are also flipped (1 = low)
  if (/leak/.test(param) || /lightning/.test(param)) {
    return true;
  }

  // All other batteries: 0 = low
  return false;
};

/**
 * Check if a specific battery field value indicates low battery
 * Used by BatteryWidget to determine which batteries are low
 */
export const isBatteryLow = (field, value, device) => {
  const lowIs1 = value === 1 || value === '1';
  const flipped = isLowBatteryFlipped(field, device);

  return flipped ? lowIs1 : !lowIs1;
};

/**
 * Convert stored alert condition/threshold back to "Low" or "OK" for display
 * Used when editing existing battery alerts
 */
export const getBatteryDisplayValue = (param, condition, threshold, device) => {
  const isFlipped = isLowBatteryFlipped(param, device);

  // For flipped batteries (1 = low):
  //   Low alert: condition = '>', threshold = 0.5
  //   OK alert: condition = '<', threshold = 0.5
  // For normal batteries (0 = low):
  //   Low alert: condition = '<', threshold = 0.5
  //   OK alert: condition = '>', threshold = 0.5

  if (isFlipped) {
    return condition === '>' ? 'Low' : 'OK';
  } else {
    return condition === '<' ? 'Low' : 'OK';
  }
};

/**
 * Convert battery status selection ("Low" or "OK") to alert condition and threshold
 * Used when saving battery alerts
 */
export const getBatteryAlertConfig = (batteryStatus, param, device) => {
  const isFlipped = isLowBatteryFlipped(param, device);

  // Always use 0.5 as threshold (between 0 and 1)
  const threshold = 0.5;

  // Determine condition based on whether low=1 or low=0
  let condition;
  if (batteryStatus === 'Low') {
    condition = isFlipped ? '>' : '<';
  } else { // 'OK'
    condition = isFlipped ? '<' : '>';
  }

  return { condition, threshold };
};

/**
 * Check if a parameter is a leak field
 */
export const isLeakParam = (param) => {
  return WIDGET_CONFIG.leak.fields.includes(param);
};

/**
 * Convert stored alert condition/threshold back to leak status label for display
 * Used when editing existing leak alerts
 * Leak states: 0 = OK, 1 = LEAK, 2 = OFFLINE
 */
export const getLeakDisplayValue = (condition, threshold) => {
  // We use = condition with exact threshold values
  if (condition === '=' && threshold === 0) {
    return 'OK';
  } else if (condition === '=' && threshold === 1) {
    return 'LEAK';
  } else if (condition === '=' && threshold === 2) {
    return 'OFFLINE';
  }
  // Default to OK if unknown
  return 'OK';
};

/**
 * Convert leak status selection to alert condition and threshold
 * Used when saving leak alerts
 * Leak states: 0 = OK, 1 = LEAK, 2 = OFFLINE
 */
export const getLeakAlertConfig = (leakStatus) => {
  const condition = '=';
  let threshold;

  switch (leakStatus) {
    case 'OK':
      threshold = 0;
      break;
    case 'LEAK':
      threshold = 1;
      break;
    case 'OFFLINE':
      threshold = 2;
      break;
    default:
      threshold = 0;
  }

  return { condition, threshold };
};
